/**
 * Renderer-side orchestration for Docker auto-discovery (Workstream B3):
 * availability probe, running one explicit + stateless detection, opening the
 * results dialog, and saving an added candidate. Nothing auto-connects — Add
 * only saves to the connections list.
 */
import type {
  DockerAvailability,
  DockerCandidate,
  DockerDetectRequest,
  DockerDetectResult,
  LocalSqlServerInstance,
} from '../../../types/dockerDiscovery';
import { useConnectionStore } from '../store/connectionStore';
import { useDockerDiscoveryStore, type DockerDiscoverySource } from './dockerDiscoveryStore';
import { buildConnectionFromDockerCandidate, countBucket } from './dockerDiscoveryUtils';

/**
 * E2E seam: in test mode a fake Docker socket path may be stashed in
 * localStorage; detection routes through it via an explicit override the main
 * process only honours in test builds. No effect in production (key unset).
 */
function testSocketOverride(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    if (window.electronAPI?.testMode?.isTestMode?.() !== true) return null;
    return window.localStorage.getItem('sql-studio-e2e-docker-socket-path');
  } catch {
    return null;
  }
}

/**
 * E2E seam (Wave C / task C4): in test mode, canned native local SQL Server
 * instances may be stashed as JSON in localStorage so e2e specs can exercise
 * the "Local" badge / merge / Add flow without a real Windows registry. The
 * main process only honours this field in test builds and bypasses the win32
 * platform guard when it is present. No effect in production (key unset).
 */
function testLocalInstancesOverride(): LocalSqlServerInstance[] | null {
  try {
    if (typeof window === 'undefined') return null;
    if (window.electronAPI?.testMode?.isTestMode?.() !== true) return null;
    const raw = window.localStorage.getItem('sql-studio-e2e-local-instances-override');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LocalSqlServerInstance[]) : null;
  } catch {
    return null;
  }
}

/** Probe whether the local Docker Engine socket/pipe exists. */
export async function checkDockerAvailability(): Promise<DockerAvailability> {
  const api = window.electronAPI?.dockerDiscovery;
  if (!api) return { available: false, socketPath: '' };
  try {
    return await api.availability();
  } catch {
    return { available: false, socketPath: '' };
  }
}

/** Run one detection over the local Docker socket (or the test overrides). */
export async function runDockerDetect(): Promise<DockerDetectResult> {
  const api = window.electronAPI?.dockerDiscovery;
  if (!api) return { outcome: 'no_docker', candidates: [] };
  const socketOverride = testSocketOverride();
  const localOverride = testLocalInstancesOverride();
  const request: DockerDetectRequest = {};
  if (socketOverride) request.socketPathOverride = socketOverride;
  if (localOverride) request.localInstancesOverride = localOverride;
  return api.detect(Object.keys(request).length > 0 ? request : undefined);
}

/**
 * Open the results dialog in its detecting state, run detection, then drop the
 * result in. Fires `feature:docker_detect` once the result comes back.
 */
export async function openDockerDiscoveryDialog(
  source: DockerDiscoverySource,
  /** Where in the New Connection dialog Detect was used (Option C, C1; analytics only). */
  placement?: 'start' | 'rail' | 'compact',
): Promise<void> {
  const store = useDockerDiscoveryStore.getState();
  store.openDetecting(source);
  const result = await runDockerDetect();
  store.setResult(result);
  const localCount = result.candidates.filter((c) => c.kind === 'local').length;
  window.electronAPI?.analytics
    ?.track('feature:docker_detect', {
      outcome: result.outcome,
      count_bucket: countBucket(result.candidates.length),
      local_count_bucket: countBucket(localCount),
      ...(placement ? { placement } : {}),
    })
    ?.catch(() => {});

  // Azurite gets its own event (spec §12.1) so the storage funnel is readable
  // on its own; bucketed like every other count here, never a raw number.
  const azuriteCount = result.candidates.filter((c) => c.engine === 'azurestorage').length;
  if (azuriteCount > 0) {
    window.electronAPI?.analytics
      ?.track('feature:storage_docker_azurite_detected', { count: countBucket(azuriteCount) })
      ?.catch(() => {});
  }
}

/**
 * Save an added candidate as a connection (never connects). Fires
 * `feature:docker_add` on success. Returns whether the save succeeded.
 */
export async function addDockerCandidate(candidate: DockerCandidate): Promise<boolean> {
  const { addConnection } = useConnectionStore.getState();
  const connection = buildConnectionFromDockerCandidate(candidate);
  const ok = await addConnection(connection);
  if (ok) {
    window.electronAPI?.analytics
      ?.track('feature:docker_add', {
        engine: candidate.engine,
        has_password: Boolean(candidate.password),
      })
      ?.catch(() => {});
  }
  return ok;
}
